import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import {
  AiModel,
  CreateAiModelPayload,
  UpdateAiModelPayload,
} from "../models/ai-model.model";
import {
  AiQuota,
  CreateAiQuotaPayload,
  UpdateAiQuotaPayload,
} from "../models/ai-quota.model";
import {
  AiCheckResult,
  AiUsageFilterOptions,
  AiUsagePage,
} from "../models/ai-usage.model";

export type AiEntityType = "client" | "provider";

export interface AiUsageListParams {
  page?: number;
  perPage?: number;
  startDate?: string;
  endDate?: string;
  /** Multi-select: values within one filter are OR-ed, the filters AND-ed. */
  features?: string[];
  /** Model ids, not identifiers — identifiers are not unique across providers. */
  modelIds?: number[];
  /** User ids. Filtering on the display name would break on a rename. */
  userIds?: string[];
}

/**
 * Only the date window: the filter options must not narrow as filters are
 * applied, or choosing a feature would empty the other dropdowns.
 */
export interface AiUsageFilterParams {
  startDate?: string;
  endDate?: string;
}

@Injectable({ providedIn: "root" })
export class AiTokensApiService {
  private base = `${environment.gateway_endpoint}admin/ai-tokens`;

  constructor(private http: HttpClient) {}

  listModels(): Observable<AiModel[]> {
    return this.http.get<AiModel[]>(`${this.base}/models`);
  }

  createModel(payload: CreateAiModelPayload): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.base}/models`, payload);
  }

  updateModel(id: number, payload: UpdateAiModelPayload): Observable<void> {
    return this.http.patch<void>(`${this.base}/models/${id}`, payload);
  }

  deleteModel(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/models/${id}`);
  }

  listQuotas(
    entityType: AiEntityType,
    entityId: string
  ): Observable<AiQuota[]> {
    return this.http.get<AiQuota[]>(
      `${this.base}/${entityType}/${entityId}/quotas`
    );
  }

  createQuota(
    entityType: AiEntityType,
    entityId: string,
    payload: CreateAiQuotaPayload
  ): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(
      `${this.base}/${entityType}/${entityId}/quotas`,
      payload
    );
  }

  updateQuota(
    entityType: AiEntityType,
    entityId: string,
    id: number,
    payload: UpdateAiQuotaPayload
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.base}/${entityType}/${entityId}/quotas/${id}`,
      payload
    );
  }

  checkStatus(
    entityType: AiEntityType,
    entityId: string
  ): Observable<AiCheckResult> {
    return this.http.post<AiCheckResult>(
      `${this.base}/${entityType}/${entityId}/check`,
      {}
    );
  }

  listUsage(
    entityType: AiEntityType,
    entityId: string,
    params: AiUsageListParams = {}
  ): Observable<AiUsagePage> {
    let httpParams = new HttpParams();
    if (params.page !== undefined)
      httpParams = httpParams.set("page", String(params.page));
    if (params.perPage !== undefined)
      httpParams = httpParams.set("perPage", String(params.perPage));
    if (params.startDate)
      httpParams = httpParams.set("startDate", params.startDate);
    if (params.endDate) httpParams = httpParams.set("endDate", params.endDate);

    // `name[]` rather than a repeated bare `name`: the gateway is PHP, which
    // keeps only the LAST occurrence of a repeated plain key and would silently
    // collapse a multi-selection to one value. The brackets are what make it an
    // array there; Laravel then forwards it to xpath-tokens as `name[0]=…`,
    // which Express's query parser reads back as an array.
    httpParams = this.appendAll(httpParams, "features", params.features);
    httpParams = this.appendAll(httpParams, "modelIds", params.modelIds);
    httpParams = this.appendAll(httpParams, "userIds", params.userIds);

    return this.http.get<AiUsagePage>(
      `${this.base}/${entityType}/${entityId}/usage`,
      { params: httpParams }
    );
  }

  private appendAll(
    httpParams: HttpParams,
    name: string,
    values?: Array<string | number>
  ): HttpParams {
    (values ?? []).forEach((value) => {
      httpParams = httpParams.append(`${name}[]`, String(value));
    });

    return httpParams;
  }

  listUsageFilters(
    entityType: AiEntityType,
    entityId: string,
    params: AiUsageFilterParams = {}
  ): Observable<AiUsageFilterOptions> {
    const httpParams: Record<string, string> = {};
    if (params.startDate) httpParams["startDate"] = params.startDate;
    if (params.endDate) httpParams["endDate"] = params.endDate;

    return this.http.get<AiUsageFilterOptions>(
      `${this.base}/${entityType}/${entityId}/usage/filters`,
      { params: httpParams }
    );
  }
}
